import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react"
import axiosInstance from "../../lib/api-client"
import { API_ENDPOINTS } from "../../lib/api-endpoint"
import { CV_UPLOAD_OPTIONS } from "../cv-upload/types"
import { CandidateStatusMap, FilterType, TalentType, ViewMode } from "./types"

// Define available options for filters
export const filterOptions = {
  levels: ["Junior", "Middle", "Senior", "Lead", "Principal", "Director"],
  statuses: Object.values(CandidateStatusMap),
  skills: CV_UPLOAD_OPTIONS.skills.map(skill => skill.value),
  languages: CV_UPLOAD_OPTIONS.languages.map(language => language.value),
}

type TalentContextType = {
  talents: TalentType[]
  filteredTalents: TalentType[]
  viewMode: ViewMode
  searchQuery: string
  filters: FilterType
  selectedTalent: TalentType | null
  setViewMode: (mode: ViewMode) => void
  setSearchQuery: (query: string) => void
  setFilters: (filters: FilterType) => void
  resetFilters: () => void
  setSelectedTalent: (talentId: string | null) => void
  addTalent: (talent: Omit<TalentType, "id">) => void
  updateTalent: (id: string, talentData: Partial<TalentType>) => void
  deleteTalent: (id: string) => Promise<void>
  isFiltering: boolean
  isLoading: boolean
  error: Error | null
}

export const TalentContext = createContext<TalentContextType | undefined>(undefined)

type TalentProviderProps = {
  children: ReactNode
}

// Function to fetch candidates from API
const fetchCandidates = async (): Promise<TalentType[]> => {
  const response = await axiosInstance.get(API_ENDPOINTS.CANDIDATE);
  return response.data;
};

export function TalentProvider({ children }: TalentProviderProps) {
  // Replace useState with useQuery
  const { 
    data: talents = [], 
    isLoading, 
    error 
  } = useQuery<TalentType[], Error>({
    queryKey: ['candidates'],
    queryFn: fetchCandidates,
  });
  
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState<ViewMode>("grid")
  const [searchQuery, setSearchQuery] = useState("")
  const [filters, setFiltersState] = useState<FilterType>({
    level: "all",
    status: "all",
    skills: [],
    languages: []
  })
  const [selectedTalent, setSelectedTalentInternal] = useState<TalentType | null>(null)
  const [isFiltering, setIsFiltering] = useState(false)
  
  // Calculate if any filters are active
  useEffect(() => {
    const hasActiveFilters = 
      (!!filters.level && filters.level !== "all") || 
      (!!filters.status && filters.status !== "all") || 
      (Array.isArray(filters.skills) && filters.skills.length > 0) || 
      (Array.isArray(filters.languages) && filters.languages.length > 0) ||
      !!filters.dateRange;
    
    setIsFiltering(hasActiveFilters);
  }, [filters])

  // Apply filters whenever search query or filters change
  // Using useMemo instead of useEffect to prevent infinite updates
  const filteredTalents = useMemo(() => {
    return talents.filter(talent => {
      // Search filter
      const matchesSearch = 
        !searchQuery || 
        talent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        talent.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        talent.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Level filter
      const matchesLevel = !filters.level || filters.level === "all" || talent.level === filters.level;
      
      // Status filter
      const matchesStatus = !filters.status || filters.status === "all" || talent.status === filters.status;
      
      // Skills filter - match if ALL selected skills are in talent's skills
      const matchesSkills = 
        !Array.isArray(filters.skills) || 
        filters.skills.length === 0 || 
        filters.skills.every(skill => talent.skills.includes(skill));
      
      // Languages filter - match if ANY selected language is in talent's languages
      const matchesLanguages = 
        !Array.isArray(filters.languages) || 
        filters.languages.length === 0 || 
        filters.languages.some(lang => talent.languages.some(tLang => tLang.language === lang));
      
      // Date filter
      const matchesDate = !filters.dateRange || talent.lastUpdated === filters.dateRange.start;
      
      return matchesSearch && matchesLevel && matchesStatus && matchesSkills && matchesLanguages && matchesDate;
    });
  }, [talents, searchQuery, filters]);

  const setFilters = (newFilters: FilterType) => {
    // Ensure arrays are properly initialized
    const sanitizedFilters = {
      ...newFilters,
      skills: Array.isArray(newFilters.skills) ? newFilters.skills : [],
      languages: Array.isArray(newFilters.languages) ? newFilters.languages : []
    };
    
    setFiltersState(sanitizedFilters);
  };

  const resetFilters = () => {
    const emptyFilters = {
      level: "all",
      status: "all",
      skills: [],
      languages: []
    };
    
    setFiltersState(emptyFilters);
  };

  const setSelectedTalent = (talentId: string | null) => {
    if (talentId === null) {
      setSelectedTalentInternal(null);
    } else {
      const talent = talents.find(t => t._id === talentId) || null;
      setSelectedTalentInternal(talent);
    }
  };
  
  // Add talent mutation
  const addTalentMutation = useMutation({
    mutationFn: (talent: Omit<TalentType, "id">) => {
      return axiosInstance.post(API_ENDPOINTS.CANDIDATE, talent);
    },
    onSuccess: () => {
      // Invalidate and refetch the candidates list
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
  
  // Update talent mutation
  const updateTalentMutation = useMutation({
    mutationFn: ({ id, talentData }: { id: string, talentData: Partial<TalentType> }) => {
      return axiosInstance.put(`${API_ENDPOINTS.CANDIDATE}/${id}`, talentData);
    },
    onSuccess: () => {
      // Invalidate and refetch the candidates list
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
  
  // Delete talent mutation
  const deleteTalentMutation = useMutation({
    mutationFn: (id: string) => {
      return axiosInstance.delete(`${API_ENDPOINTS.CANDIDATE}/${id}`);
    },
    onSuccess: () => {
      // Invalidate and refetch the candidates list
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });

  const addTalent = (talent: Omit<TalentType, "id">) => {
    addTalentMutation.mutate(talent);
  };
  
  const updateTalent = (id: string, talentData: Partial<TalentType>) => {
    updateTalentMutation.mutate({ id, talentData });
  };
  
  const deleteTalent = async (id: string) => {
    await deleteTalentMutation.mutateAsync(id);
    // If the deleted talent was selected, clear the selection
    if (selectedTalent && selectedTalent._id === id) {
      setSelectedTalentInternal(null);
    }
  };

  return (
    <TalentContext.Provider
      value={{
        talents,
        filteredTalents,
        viewMode,
        searchQuery,
        filters,
        selectedTalent,
        setViewMode,
        setSearchQuery,
        setFilters,
        resetFilters,
        setSelectedTalent,
        addTalent,
        updateTalent,
        deleteTalent,
        isFiltering,
        isLoading,
        error
      }}
    >
      {children}
    </TalentContext.Provider>
  );
}

export function useTalent() {
  const context = useContext(TalentContext)
  if (context === undefined) {
    throw new Error("useTalent must be used within a TalentProvider")
  }
  return context
} 